/**
 * Paper sizes. Dimensions are PORTRAIT millimeters (width ≤ height); the
 * page orientation swaps them.
 */

export const PAPER_SIZES = {
  A0: { label: 'A0', widthMm: 841, heightMm: 1189 },
  A1: { label: 'A1', widthMm: 594, heightMm: 841 },
  A2: { label: 'A2', widthMm: 420, heightMm: 594 },
  A3: { label: 'A3', widthMm: 297, heightMm: 420 },
  A4: { label: 'A4', widthMm: 210, heightMm: 297 },
  A5: { label: 'A5', widthMm: 148, heightMm: 210 },
  ANSI_A: { label: 'ANSI A (Letter)', widthMm: 215.9, heightMm: 279.4 },
  ANSI_B: { label: 'ANSI B (Tabloid)', widthMm: 279.4, heightMm: 431.8 },
  ANSI_C: { label: 'ANSI C', widthMm: 431.8, heightMm: 558.8 },
  ANSI_D: { label: 'ANSI D', widthMm: 558.8, heightMm: 863.6 },
  ANSI_E: { label: 'ANSI E', widthMm: 863.6, heightMm: 1117.6 },
  ARCH_A: { label: 'Arch A', widthMm: 228.6, heightMm: 304.8 },
  ARCH_B: { label: 'Arch B', widthMm: 304.8, heightMm: 457.2 },
  ARCH_C: { label: 'Arch C', widthMm: 457.2, heightMm: 609.6 },
  ARCH_D: { label: 'Arch D', widthMm: 609.6, heightMm: 914.4 },
  ARCH_E1: { label: 'Arch E1', widthMm: 762, heightMm: 1066.8 },
  ARCH_E: { label: 'Arch E', widthMm: 914.4, heightMm: 1219.2 },
} as const satisfies Record<string, { label: string; widthMm: number; heightMm: number }>;

export type PaperId = keyof typeof PAPER_SIZES;

export const PAPER_IDS = Object.keys(PAPER_SIZES) as PaperId[];

export type PaperOrientation = 'portrait' | 'landscape';

export type PaperRef = { kind: 'standard'; id: PaperId } | { kind: 'custom'; widthMm: number; heightMm: number };

/** Bounds for custom paper (a business card up to a long plotter roll). */
export const CUSTOM_PAPER_LIMITS = { minMm: 20, maxMm: 5000 } as const;

export interface PageSizeMm {
  widthMm: number;
  heightMm: number;
}

/** Page size after orientation. Custom paper is taken as given, then oriented the same way. */
export function pageSizeMm(paper: PaperRef, orientation: PaperOrientation): PageSizeMm {
  const { widthMm, heightMm } = paper.kind === 'standard' ? PAPER_SIZES[paper.id] : paper;
  const short = Math.min(widthMm, heightMm);
  const long = Math.max(widthMm, heightMm);
  return orientation === 'portrait' ? { widthMm: short, heightMm: long } : { widthMm: long, heightMm: short };
}

export function paperLabel(paper: PaperRef): string {
  return paper.kind === 'standard' ? PAPER_SIZES[paper.id].label : `${paper.widthMm} × ${paper.heightMm} mm`;
}
